import type {
  ActionEnvelope,
  ChatState,
  ChatSummary,
  SessionAction,
  SessionState,
  ToolCallState,
} from '@microsoft/agent-host-protocol'
import {sessionReducer} from '@microsoft/agent-host-protocol'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {normalizeSessionAction} from '@github-ui/ahp-relay/protocol-compatibility'
import {
  readCheckpoints,
  updateCheckpointAnchors,
  type CheckpointAnchors,
} from '@github-ui/ahp-relay/session-checkpoints'
import {
  getTurns,
  resolveSubagent,
  type TimelineTurn,
  type SubagentHandle,
  type SubagentResolver,
} from '@github-ui/ahp-relay/ahp-session-selectors'
import {Reassembler, type ChunkEnvelope} from '@github-ui/ahp-relay/webpubsub/chunking'
import {replayChangesetActions, type PersistedChangeset} from './replay-changeset-actions'

const IDLE_STATUS = 1 as ChatState['status']
const READY_LIFECYCLE = 'ready' as SessionState['lifecycle']
// Updates can arrive without the initial catalog; they do not establish its membership.
const CATALOG_ACTIONS = new Set(['session/chatAdded', 'session/chatRemoved'])

export interface AhpReplicationFrame {
  readonly ns?: string
  readonly seq?: number
  readonly at?: string
  readonly session_id?: string
  readonly payload?: ChunkEnvelope
}

export interface AhpSessionBundle {
  sessionState: SessionState
  chatStates: Readonly<Record<string, ChatState>>
  chatResourcesWithPersistedActions: Readonly<Record<string, true>>
  removedChatResources?: readonly string[]
  defaultChatState: ChatState | undefined
  catalogIsAuthoritative: boolean
  timelineTurns?: Readonly<Record<string, TimelineTurn[]>>
  changesetStates?: Readonly<Record<string, PersistedChangeset>>
  changesetCatalogIsAuthoritative?: boolean
  changesetCatalogError?: string
  checkpointAnchors?: CheckpointAnchors
}

function isActionEnvelope(value: unknown): value is ActionEnvelope {
  if (value === null || typeof value !== 'object') return false
  const env = value as Partial<ActionEnvelope>
  return (
    typeof env.channel === 'string' &&
    typeof env.serverSeq === 'number' &&
    !!env.action &&
    typeof env.action.type === 'string'
  )
}

function reassembleEnvelopes(frames: readonly AhpReplicationFrame[]): ActionEnvelope[] {
  const reassembler = new Reassembler()
  const envelopes: ActionEnvelope[] = []
  const ordered = [...frames].sort((a, b) => (a?.seq ?? 0) - (b?.seq ?? 0))
  for (const frame of ordered) {
    if (!frame?.payload) continue
    let payload: unknown
    try {
      payload = reassembler.ingest(frame.payload)
    } catch {
      continue
    }
    if (payload == null) continue
    if (isActionEnvelope(payload) && !payload.rejectionReason) envelopes.push(payload)
  }
  return envelopes
}

function groupChatEnvelopes(envelopes: readonly ActionEnvelope[]): Map<string, ActionEnvelope[]> {
  const byChannel = new Map<string, ActionEnvelope[]>()
  for (const env of envelopes) {
    if (!env.action.type.startsWith('chat/')) continue
    const existing = byChannel.get(env.channel)
    if (existing) {
      existing.push(env)
    } else {
      byChannel.set(env.channel, [env])
    }
  }
  return byChannel
}

function selectDefaultChat(
  chatStates: Readonly<Record<string, ChatState>>,
  byChannel: ReadonlyMap<string, ActionEnvelope[]>,
  preferredChannels: ReadonlyArray<string | undefined>,
  allowedChannels?: ReadonlySet<string>,
): ChatState | undefined {
  for (const channel of preferredChannels) {
    if (!channel || (allowedChannels && !allowedChannels.has(channel))) continue
    const state = chatStates[channel]
    if (state) return state
  }

  let best: string | undefined
  let bestCount = -1
  const roots = new Set(
    [...byChannel.keys()].filter(channel => {
      if (allowedChannels && !allowedChannels.has(channel)) return false
      const origin = chatStates[channel]?.origin
      return (
        origin?.kind !== 'tool' || !chatStates[origin.chat] || (allowedChannels && !allowedChannels.has(origin.chat))
      )
    }),
  )
  for (const [channel, envs] of byChannel) {
    if (allowedChannels && !allowedChannels.has(channel)) continue
    if (roots.size > 0 && !roots.has(channel)) continue
    if (envs.length > bestCount) {
      best = channel
      bestCount = envs.length
    }
  }
  return best ? chatStates[best] : undefined
}

export interface RebuildOptions {
  sessionChannel?: string
  chatChannel: string
  title?: string
  modifiedAt?: string
  settleInterruptedTurn?: (state: ChatState) => ChatState
  preserveInterruptedTurns?: boolean
}

function createSessionSeed(title: string): SessionState {
  return {
    provider: '',
    title,
    status: IDLE_STATUS,
    lifecycle: READY_LIFECYCLE,
    activeClients: [],
    chats: [],
  }
}

function createChatSeed(
  channel: string,
  summary: ChatSummary | undefined,
  title: string,
  modifiedAt: string,
): ChatState {
  return {
    resource: channel,
    title: summary?.title ?? title,
    status: summary?.status ?? IDLE_STATUS,
    modifiedAt: summary?.modifiedAt ?? modifiedAt,
    turns: [],
    ...(summary?.activity ? {activity: summary.activity} : {}),
    ...(summary?.origin ? {origin: summary.origin} : {}),
    ...(summary?.interactivity ? {interactivity: summary.interactivity} : {}),
    ...(summary?.workingDirectories ? {workingDirectories: summary.workingDirectories} : {}),
  }
}

function foldChatState(
  seed: ChatState,
  envelopes: readonly ActionEnvelope[],
  settleInterruptedTurn?: (state: ChatState) => ChatState,
): ChatState {
  const store = new AhpChatStore()
  store.start()
  store.applySnapshot(seed)
  for (const envelope of envelopes) {
    const state = store.getSnapshot()
    if (
      settleInterruptedTurn &&
      state?.activeTurn &&
      envelope.action.type === 'chat/turnStarted' &&
      state.activeTurn.id !== envelope.action.turnId
    ) {
      store.applySnapshot(settleInterruptedTurn(state))
    }
    store.applyAction(envelope)
  }
  return store.getSnapshot() ?? seed
}

export function rebuildAhpSessionBundleFromEvents(
  frames: readonly AhpReplicationFrame[],
  options: RebuildOptions,
): AhpSessionBundle | undefined {
  const envelopes = reassembleEnvelopes(frames).sort((a, b) => a.serverSeq - b.serverSeq)
  const modifiedAt = [...frames].reverse().find(frame => frame.at)?.at ?? options.modifiedAt
  return rebuildAhpSessionBundleFromActions(envelopes, {...options, modifiedAt})
}

/** Fold already decoded actions in producer order, including transport restart epochs. */
export function rebuildAhpSessionBundleFromActions(
  envelopes: readonly ActionEnvelope[],
  options: RebuildOptions,
): AhpSessionBundle | undefined {
  const {chatChannel, title = '', modifiedAt = ''} = options
  const sessionChannel = options.sessionChannel ?? chatChannel.replace(/\/chat(?:\/.*)?$/, '')
  if (envelopes.length === 0) return undefined

  let sessionState = createSessionSeed(title)
  let catalogIsAuthoritative = false
  let changesetCatalogIsAuthoritative = false
  let changesetCatalogError: string | undefined
  const removedChatResources = new Set<string>()
  const sessionEnvelopes = envelopes.filter(
    envelope => envelope.channel === sessionChannel && envelope.action.type.startsWith('session/'),
  )

  for (const envelope of sessionEnvelopes) {
    const action = envelope.action as SessionAction
    if (CATALOG_ACTIONS.has(action.type)) catalogIsAuthoritative = true
    if (action.type === 'session/changesetsChanged') {
      if (
        action.changesets !== undefined &&
        (!Array.isArray(action.changesets) ||
          !action.changesets.every(
            entry =>
              typeof entry === 'object' &&
              entry !== null &&
              !Array.isArray(entry) &&
              typeof entry.uriTemplate === 'string' &&
              entry.uriTemplate.trim(),
          ))
      ) {
        changesetCatalogError =
          'Some information about changed files may be incomplete. Reconnect to refresh the changed files.'
        continue
      }
      changesetCatalogIsAuthoritative = true
      changesetCatalogError = undefined
    }
    if (action.type === 'session/chatAdded') {
      removedChatResources.delete(action.summary.resource)
    } else if (action.type === 'session/chatRemoved') {
      removedChatResources.add(action.chat)
    }
    sessionState = sessionReducer(sessionState, normalizeSessionAction(action))
  }

  const byChannel = groupChatEnvelopes(envelopes)
  const chatResourcesWithPersistedActions: Record<string, true> = {}
  for (const channel of byChannel.keys()) chatResourcesWithPersistedActions[channel] = true
  for (const summary of sessionState.chats) {
    if (!byChannel.has(summary.resource)) byChannel.set(summary.resource, [])
  }

  const summaries = new Map(sessionState.chats.map(summary => [summary.resource, summary]))
  const chatStates: Record<string, ChatState> = {}
  const timelineTurns: Record<string, TimelineTurn[]> = {}
  for (const [channel, chatEnvelopes] of byChannel) {
    const summary = summaries.get(channel)
    const seedTitle = channel === chatChannel ? sessionState.title || title : (summary?.title ?? channel)
    // A rehost may start a different turn without a terminal event for the old
    // one. Preserve its display position without inventing a terminal state.
    const savedTurns = new Map<string, TimelineTurn | undefined>()
    if (options.preserveInterruptedTurns) {
      for (const {action} of chatEnvelopes) {
        if (action.type === 'chat/turnStarted' && !savedTurns.has(action.turnId)) {
          savedTurns.set(action.turnId, undefined)
        }
      }
    }
    chatStates[channel] = foldChatState(
      createChatSeed(channel, summary, seedTitle, modifiedAt),
      chatEnvelopes,
      options.preserveInterruptedTurns
        ? state => {
            if (state.activeTurn) savedTurns.set(state.activeTurn.id, state.activeTurn)
            return state
          }
        : options.settleInterruptedTurn,
    )
    if (options.preserveInterruptedTurns) {
      for (const turn of getTurns(chatStates[channel])) savedTurns.set(turn.id, turn)
      timelineTurns[channel] = [...savedTurns.values()].filter((turn): turn is TimelineTurn => turn !== undefined)
    }
  }

  const defaultChatState = selectDefaultChat(
    chatStates,
    byChannel,
    [sessionState.defaultChat, chatChannel],
    // A persisted catalog can omit the initial root. Only explicit removal
    // or hidden state excludes a reconstructed chat from selection.
    new Set(
      Object.values(chatStates)
        .filter(state => !removedChatResources.has(state.resource) && state.interactivity !== 'hidden')
        .map(state => state.resource),
    ),
  )
  let checkpointAnchors: CheckpointAnchors = new Map()
  if (defaultChatState) {
    const store = new AhpChatStore()
    store.start()
    const summary = summaries.get(defaultChatState.resource)
    const seedTitle =
      defaultChatState.resource === chatChannel
        ? sessionState.title || title
        : (summary?.title ?? defaultChatState.resource)
    store.applySnapshot(createChatSeed(defaultChatState.resource, summary, seedTitle, modifiedAt))
    for (const envelope of envelopes) {
      if (envelope.channel === defaultChatState.resource && envelope.action.type.startsWith('chat/')) {
        const state = store.getSnapshot()
        if (
          !options.preserveInterruptedTurns &&
          options.settleInterruptedTurn &&
          state?.activeTurn &&
          envelope.action.type === 'chat/turnStarted' &&
          state.activeTurn.id !== envelope.action.turnId
        ) {
          store.applySnapshot(options.settleInterruptedTurn(state))
        }
        store.applyAction(envelope)
      } else if (envelope.channel === sessionChannel && envelope.action.type === 'session/metaChanged') {
        const state = store.getSnapshot()
        const anchor =
          state?.activeTurn?.id ??
          state?.turns.at(-1)?.id ??
          (state && state.turnsNextCursor === undefined ? null : undefined)
        checkpointAnchors = updateCheckpointAnchors(
          checkpointAnchors,
          readCheckpoints(envelope.action._meta),
          () => anchor,
        )
      }
    }
    store.dispose()
  }

  return {
    sessionState,
    chatStates,
    chatResourcesWithPersistedActions,
    removedChatResources: [...removedChatResources],
    defaultChatState,
    catalogIsAuthoritative,
    changesetStates: replayChangesetActions(envelopes),
    changesetCatalogIsAuthoritative,
    changesetCatalogError,
    checkpointAnchors,
    ...(options.preserveInterruptedTurns ? {timelineTurns} : {}),
  }
}

export function rebuildChatStateFromEvents(
  frames: readonly AhpReplicationFrame[],
  options: RebuildOptions,
): ChatState | undefined {
  return rebuildAhpSessionBundleFromEvents(frames, options)?.defaultChatState
}

export function resolvePersistedSubagent(
  bundle: AhpSessionBundle,
  toolCall: ToolCallState,
  parentChatResource: string | undefined,
): SubagentHandle | undefined {
  return resolveSubagent(toolCall, bundle.sessionState.chats, parentChatResource, bundle.catalogIsAuthoritative)
}

export const createPersistedSubagentResolver =
  (bundle: AhpSessionBundle): SubagentResolver =>
  (toolCall, parentChatResource) =>
    resolvePersistedSubagent(bundle, toolCall, parentChatResource)

export function getSavedRootChats(bundle: AhpSessionBundle): ChatState[] {
  const resolve = createPersistedSubagentResolver(bundle)
  const nested = new Set<string>()
  for (const state of Object.values(bundle.chatStates)) {
    const parent = state.origin?.kind === 'tool' ? bundle.chatStates[state.origin.chat] : undefined
    if (parent && parent.interactivity !== 'hidden' && !bundle.removedChatResources?.includes(parent.resource)) {
      nested.add(state.resource)
    }
    for (const turn of bundle.timelineTurns?.[state.resource] ?? getTurns(state)) {
      for (const part of turn.responseParts) {
        if (part.kind !== 'toolCall') continue
        const subagent = resolve(part.toolCall, state.resource)
        if (subagent) nested.add(subagent.resource)
      }
    }
  }
  return Object.values(bundle.chatStates).filter(
    state =>
      state.interactivity !== 'hidden' &&
      !bundle.removedChatResources?.includes(state.resource) &&
      !nested.has(state.resource) &&
      (!bundle.catalogIsAuthoritative ||
        state.resource === bundle.defaultChatState?.resource ||
        bundle.sessionState.chats.some(chat => chat.resource === state.resource) ||
        bundle.chatResourcesWithPersistedActions[state.resource]),
  )
}

/** Presentation-only union; a saved active turn must not replace the live root. */
export function projectSavedTimelineTurns(
  saved: readonly TimelineTurn[],
  live: readonly TimelineTurn[],
): TimelineTurn[] {
  const turns = new Map(saved.map(turn => [turn.id, turn]))
  for (const turn of live) turns.set(turn.id, turn)
  return [...turns.values()]
}
